import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FileCode, ImagePlus, Eye, Pencil, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ArticleBody } from "@/components/help/helpShared";

const upload = (path, file) => { const fd = new FormData(); fd.append("file", file); return api.post(path, fd, { headers: { "Content-Type": "multipart/form-data" } }); };

export function BodyEditor({ edit, setEdit }) {
  const ta = useRef(null);
  const htmlIn = useRef(null);
  const imgIn = useRef(null);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(false);
  const fmt = edit.body_format || "markdown";

  const importHtml = async (file) => {
    if (!file) return;
    if (edit.body?.trim() && !window.confirm("Replace the current body with the uploaded HTML?")) return;
    setBusy("html");
    try {
      const { data } = await upload("/help/admin/import-html", file);
      setEdit((e) => ({ ...e, body: data.body, body_format: "html", title: e.title?.trim() ? e.title : data.title || e.title }));
      toast.success(data.skipped_images ? `HTML imported. ${data.skipped_images} image(s) with local paths were removed — use Insert image to add them.` : "HTML imported. Unsafe content was removed.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); if (htmlIn.current) htmlIn.current.value = ""; }
  };

  const insertImage = async (file) => {
    if (!file) return;
    setBusy("img");
    try {
      const { data } = await upload("/help/admin/images", file);
      const snippet = fmt === "html" ? `<p><img src="${data.url}" alt="${(data.alt || "").replace(/"/g, "")}" /></p>` : `\n![${data.alt || "image"}](${data.url})\n`;
      const el = ta.current;
      const body = edit.body || "";
      const at = el && !preview ? el.selectionStart : body.length;
      setEdit((e) => ({ ...e, body: body.slice(0, at) + snippet + body.slice(at) }));
      toast.success("Image inserted");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); if (imgIn.current) imgIn.current.value = ""; }
  };

  return (
    <div data-testid="help-body-editor">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <Label className="text-xs">Body</Label>
        <div className="flex flex-wrap items-center gap-1.5">
          <select value={fmt} onChange={(e) => setEdit({ ...edit, body_format: e.target.value })} className="h-8 rounded-md border border-slate-200 px-2 text-xs" data-testid="help-body-format">
            <option value="markdown">Markdown</option><option value="html">HTML</option></select>
          <input ref={htmlIn} type="file" accept=".html,.htm,text/html" className="hidden" onChange={(e) => importHtml(e.target.files?.[0])} data-testid="help-html-file" />
          <Button type="button" size="sm" variant="outline" className="h-8 text-xs" disabled={!!busy} onClick={() => htmlIn.current?.click()} data-testid="help-upload-html">
            {busy === "html" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileCode className="w-3.5 h-3.5 mr-1" />}Upload HTML</Button>
          <input ref={imgIn} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => insertImage(e.target.files?.[0])} data-testid="help-image-file" />
          <Button type="button" size="sm" variant="outline" className="h-8 text-xs" disabled={!!busy} onClick={() => imgIn.current?.click()} data-testid="help-insert-image">
            {busy === "img" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5 mr-1" />}Insert image</Button>
          <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setPreview(!preview)} data-testid="help-body-preview-toggle">
            {preview ? <><Pencil className="w-3.5 h-3.5 mr-1" />Edit</> : <><Eye className="w-3.5 h-3.5 mr-1" />Preview</>}</Button>
        </div>
      </div>
      {preview ? (
        <div className="rounded-md border border-slate-200 p-3 max-h-[420px] overflow-auto bg-white" data-testid="help-body-preview"><ArticleBody article={edit} /></div>
      ) : (
        <Textarea ref={ta} rows={10} value={edit.body || ""} onChange={(e) => setEdit({ ...edit, body: e.target.value })} className={fmt === "html" ? "font-mono text-xs" : ""} data-testid="help-edit-body" />
      )}
      {fmt === "html" && <p className="text-[11px] text-slate-400 mt-1">HTML is cleaned when saved: scripts, styles, forms and embedded content are removed.</p>}
    </div>
  );
}
